import { getListProjectProductsQueryOptions } from '@amarantos/api-client'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ListSearchInput } from '@/components/list-search-input'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { ProductsFilters } from '@/features/products/components/products-filters'
import { ProjectProductsPanel } from '@/features/projects/components/project-products-panel'
import { projectProductsSearchSchema, toListProjectProductsParams } from '@/features/projects/utils/search'

export const Route = createFileRoute('/_authenticated/projects/$projectId/materials')({
	validateSearch: projectProductsSearchSchema,
	loaderDeps: ({ search }) => ({ search: search.search, productCategory: search.productCategory }),
	loader: ({ context: { queryClient }, params: { projectId }, deps }) => {
		queryClient.ensureQueryData(getListProjectProductsQueryOptions(projectId, toListProjectProductsParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_project_products' />
	),
	component: ProjectMaterialsRoute,
})

function ProjectMaterialsRoute() {
	const { t } = useTranslation()
	const { projectId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()

	// Dialog state lives in the URL, so the create/edit dialogs are deep-linkable.
	const closeDialog = () => {
		navigate({ search: (previous) => ({ ...previous, dialog: undefined, assetId: undefined }) })
	}

	const onSearchChange = useCallback(
		(nextValue: string) => {
			navigate({ replace: true, search: (previous) => ({ ...previous, search: nextValue }) })
		},
		[navigate],
	)

	return (
		<ProjectProductsPanel
			projectId={projectId}
			dialog={search.dialog}
			selectedId={search.assetId}
			filters={{
				value: search,
				controls: (
					<>
						<ListSearchInput
							value={search.search}
							onChange={onSearchChange}
							placeholder={t('projects.products.filter.search_placeholder')}
						/>
						<ProductsFilters />
					</>
				),
			}}
			onCreate={() => navigate({ search: (previous) => ({ ...previous, dialog: 'create' as const }) })}
			onEdit={(projectProduct) =>
				navigate({ search: (previous) => ({ ...previous, assetId: projectProduct.id, dialog: 'edit' as const }) })
			}
			onClose={closeDialog}
		/>
	)
}
